// src/components/StoricoConsumiModal.jsx
import React, { useEffect, useMemo, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "";

const StoricoConsumiModal = ({
  mode = "acquisti",
  clientCode,
  onClose,
  pagamento,
  setPagamento,
  inviaPagamento, // opzionale
  pagamentoData,
  setPagamentoData,
  // opzionali: se li passi, il componente userà data + ora separati
  pagamentoOra,
  setPagamentoOra,
}) => {
  const [movimenti, setMovimenti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [correctingId, setCorrectingId] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editingReason, setEditingReason] = useState("");
  const [editingQuantities, setEditingQuantities] = useState({});
  const [editingMethod, setEditingMethod] = useState("CONTANTI");
  const [editingPreview, setEditingPreview] = useState(null);
  const [editingLegacyConfirmed, setEditingLegacyConfirmed] = useState(false);

  const isConto = mode === "conto";

  // Il ruolo viene verificato tramite l'utente autenticato.
  // Il backend applica comunque il controllo di autorizzazione.
  const [isSede, setIsSede] = useState(false);

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("access");

    if (!token) {
      setIsSede(false);
      return () => { active = false; };
    }

    fetch(`${API_URL}/api/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Impossibile verificare il ruolo");
        return response.json();
      })
      .then((user) => {
        if (active) {
          setIsSede(["MADRE", "SEDE", "ADMIN"].includes(String(user.role || "").toUpperCase()));
        }
      })
      .catch(() => {
        if (active) setIsSede(false);
      });

    return () => { active = false; };
  }, []);
  const usingSeparateDateTime = typeof pagamentoOra === "string" && typeof setPagamentoOra === "function";

  // ---------- helpers ----------
  const formatDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return "";
    try {
      const [y, m, d] = yyyyMmDd.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return yyyyMmDd;
    }
  };
  const formatTime = (hhmm = "") => (hhmm ? (hhmm.length === 5 ? hhmm : hhmm.slice(0, 5)) : "");
  const toMoney = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ---------- normalizzazione ----------
  const normalizeItem = (raw) => {
    if (raw?.tipo === "consumo") {
      const prodotti = (raw.dettagli || []).map((p) => ({
        consumptionProductId: p.consumption_product_id ?? null,
        name: p.prodotto ?? p.product_name ?? "",
        qty: Number(p.quantita ?? p.quantity ?? 0),
        unit: Number(p.prezzo_unitario ?? p.price ?? 0),
        subtotal: Number(p.subtotal ?? Number(p.quantity || 0) * Number(p.price || 0)),
      }));
      return {
        kind: "consumo",
        id: raw.id,
        paymentMethod: raw.payment_method || "",
        corrected: !!raw.has_payment_correction,
        date: raw.data,
        time: raw.ora,
        total: Number(raw.subtotal ?? raw.total_amount ?? 0),
        paid: Number(raw.paid_amount ?? raw.pagato ?? 0),
        status: raw.status || "",
        products: prodotti,
      };
    }
    if (raw?.tipo === "pagamento") {
      return {
        kind: "pagamento",
        date: raw.data,
        time: raw.ora,
        amount: Number(raw.importo ?? raw.amount ?? 0),
        generico: !!raw.generico,
      };
    }
    if (Array.isArray(raw?.dettagli) || raw?.total_amount !== undefined) {
      const prodotti = (raw.dettagli || []).map((p) => ({
        consumptionProductId: p.consumption_product_id ?? null,
        name: p.prodotto ?? p.product_name ?? "",
        qty: Number(p.quantita ?? p.quantity ?? 0),
        unit: Number(p.prezzo_unitario ?? p.price ?? 0),
        subtotal: Number(p.subtotal ?? Number(p.quantity || 0) * Number(p.price || 0)),
      }));
      return {
        kind: "consumo",
        id: raw.id,
        paymentMethod: raw.payment_method || "",
        corrected: !!raw.has_payment_correction,
        date: (raw.date || raw.data || "").slice(0, 10),
        time: raw.ora || "",
        total: Number(raw.total_amount ?? raw.subtotal ?? 0),
        paid: Number(raw.paid_amount ?? raw.pagato ?? 0),
        status: raw.status || "",
        products: prodotti,
      };
    }
    if (raw?.amount || raw?.importo) {
      const rawDate = raw.date || raw.data || "";
      return {
        kind: "pagamento",
        date: rawDate.slice(0, 10),
        time: raw.ora || (rawDate.includes("T") ? rawDate.slice(11, 16) : ""),
        amount: Number(raw.amount ?? raw.importo ?? 0),
        generico: !!raw.generico,
      };
    }
    return null;
  };

  // ---------- fetch ----------
  const fetchStorico = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access");
      const headers = { Authorization: `Bearer ${token}` };

      const [resConsumi, resPagamenti] = await Promise.all([
        fetch(`${API_URL}/api/clienti/${clientCode}/consumi/`, { headers }),
        fetch(`${API_URL}/api/clienti/${clientCode}/pagamenti/`, { headers }),
      ]);

      const jsonConsumi = await resConsumi.json();
      const jsonPagamenti = await resPagamenti.json();

      const arrConsumi = Array.isArray(jsonConsumi?.storico) ? jsonConsumi.storico : Array.isArray(jsonConsumi) ? jsonConsumi : [];
      const arrPagamenti = Array.isArray(jsonPagamenti?.pagamenti) ? jsonPagamenti.pagamenti : Array.isArray(jsonPagamenti) ? jsonPagamenti : [];

      const normalized = [...arrConsumi, ...arrPagamenti].map(normalizeItem).filter(Boolean);
      normalized.sort((a, b) => {
        const da = (a.date || "") + " " + (a.time || "");
        const db = (b.date || "") + " " + (b.time || "");
        return db.localeCompare(da);
      });
      setMovimenti(normalized);
    } catch (err) {
      console.error("Errore fetch storico consumi:", err);
      setMovimenti([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientCode]);

  // ---------- invio pagamento ----------
  const handlePaga = async () => {
    if (sending) return; // blocco doppio invio immediato
    if (!pagamento || Number(pagamento) <= 0) return;

    let iso = null;
    if (usingSeparateDateTime) {
      if (pagamentoData) {
        const hhmm = /^\d{2}:\d{2}$/.test(pagamentoOra || "") ? pagamentoOra : "12:00";
        iso = `${pagamentoData}T${hhmm}:00`;
      }
    } else if (pagamentoData) {
      iso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(pagamentoData)
        ? `${pagamentoData}:00`
        : pagamentoData;
    }

    const payload = { amount: Number(pagamento) };
    if (iso) {
      payload.datetime = iso;
      payload.date = iso; // retrocompatibilità
    }

    setSending(true);
    try {
      let usedParent = false;
      if (typeof inviaPagamento === "function") {
        try {
          const maybe = inviaPagamento.length > 0 ? inviaPagamento(payload) : inviaPagamento();
          await maybe;
          usedParent = true;
        } catch (e) {
          console.warn("inviaPagamento custom ha dato errore, uso fallback:", e);
        }
      }

      if (!usedParent) {
        const token = localStorage.getItem("access");
        const res = await fetch(`${API_URL}/api/clienti/${clientCode}/pagamento/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await res.text());
      }

      await fetchStorico();
    } catch (e) {
      alert("Errore invio pagamento");
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  // ---------- rettifica unificata acquisto ----------
  const apriModificaAcquisto = (consumo) => {
    if (!consumo?.id || !consumo.products?.length ||
        consumo.products.some(p => !p.consumptionProductId)) {
      alert("Impossibile modificare: dettagli prodotti incompleti.");
      return;
    }
    const quantities = {};
    consumo.products.forEach(p => {
      quantities[p.consumptionProductId] = String(p.qty);
    });
    setEditingPurchase(consumo);
    setEditingQuantities(quantities);
    setEditingMethod(consumo.paymentMethod || "CONTANTI");
    setEditingLegacyConfirmed(false);
    setEditingReason("");
    setEditingPreview(null);
  };

  const creaPayloadModifica = () => {
    const reason = editingReason.trim();
    if (reason.length < 5 || reason.length > 1000) {
      throw new Error("Inserisci una motivazione da 5 a 1000 caratteri.");
    }
    const items = editingPurchase.products.map(p => {
      const quantity = String(
        editingQuantities[p.consumptionProductId] ?? ""
      ).trim().replace(",", ".");
      if (!/^\d+(?:\.\d{1,2})?$/.test(quantity) ||
          Number(quantity) <= 0 || Number(quantity) > 9999.99) {
        throw new Error(`Quantità non valida per ${p.name}.`);
      }
      return { consumption_product_id: p.consumptionProductId, quantity };
    });
    const changed = editingPurchase.products.some(p =>
      Number(String(editingQuantities[p.consumptionProductId]).replace(",", ".")) !== Number(p.qty)
    );
    if (!changed && editingMethod === (editingPurchase.paymentMethod || "CONTANTI")) {
      throw new Error("Non hai modificato quantità o metodo di pagamento.");
    }
    if (!editingPurchase.paymentMethod && !editingLegacyConfirmed) {
      throw new Error("Conferma la verifica del pagamento storico.");
    }
    return {
      reason,
      items,
      payment_method: editingMethod,
      confirm_legacy_cash: !editingPurchase.paymentMethod && editingLegacyConfirmed,
    };
  };

  const richiestaModifica = async (azione, payload) => {
    const token = localStorage.getItem("access");
    const response = await fetch(
      `${API_URL}/api/clienti/${encodeURIComponent(clientCode)}/consumi/${editingPurchase.id}/modifica-unificata/${azione}/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );
    const result = await response.json();
    if (!response.ok) throw new Error(result.detail || "Operazione non riuscita.");
    return result;
  };

  const anteprimaModificaAcquisto = async () => {
    if (!editingPurchase || correctingId !== null) return;
    try {
      const payload = creaPayloadModifica();
      setCorrectingId(editingPurchase.id);
      setEditingPreview(null);
      const preview = await richiestaModifica("anteprima", payload);
      if (
        !preview ||
        !/^[0-9a-f]{64}$/.test(String(preview.fingerprint || "")) ||
        !preview.purchase ||
        !preview.accounting ||
        !preview.accounting.wallet ||
        !Number.isFinite(Number(preview.purchase.current_total)) ||
        !Number.isFinite(Number(preview.purchase.corrected_total)) ||
        !Number.isFinite(Number(preview.accounting.cash_delta)) ||
        !Number.isFinite(Number(preview.accounting.points_delta)) ||
        !Number.isFinite(Number(preview.accounting.wallet.promo_delta)) ||
        !Number.isFinite(Number(preview.accounting.wallet.normal_delta))
      ) {
        throw new Error("Risposta contabile incompleta: rettifica bloccata.");
      }
      setEditingPreview({ preview, payload });
    } catch (error) {
      alert(`Anteprima non disponibile: ${error.message}`);
    } finally {
      setCorrectingId(null);
    }
  };

  const salvaModificaAcquisto = async () => {
    if (!editingPurchase || !editingPreview || correctingId !== null) return;
    const { preview, payload } = editingPreview;
    const cashDelta = Number(preview.accounting?.cash_delta ?? 0);
    const messaggio = cashDelta > 0
      ? `DICHIARAZIONE DI INCASSO\\n\\nHo già INCASSATO €${toMoney(cashDelta)} in contanti dal cliente.\\n\\nPremendo OK confermo che il denaro è stato effettivamente ricevuto.`
      : cashDelta < 0
        ? `DICHIARAZIONE DI RIMBORSO\\n\\nHo già RIMBORSATO €${toMoney(-cashDelta)} in contanti al cliente.\\n\\nPremendo OK confermo che il denaro è stato effettivamente restituito.`
        : "Nessun movimento di contanti da confermare.";

    if (!window.confirm(
      `Confermi la rettifica dell'acquisto #${editingPurchase.id}?\n${messaggio}\nWallet e punti fedeltà saranno aggiornati.`
    )) return;

    try {
      setCorrectingId(editingPurchase.id);
      const result = await richiestaModifica("conferma", {
        ...payload,
        fingerprint: preview.fingerprint,
        confirm_cash_received: cashDelta > 0,
        confirm_cash_refunded: cashDelta < 0,
      });
      alert(
        `Acquisto modificato correttamente!\nNuovo totale: €${toMoney(result.corrected_total ?? preview.purchase?.corrected_total)}.`
      );
      setEditingPurchase(null);
      setEditingPreview(null);
      setEditingReason("");
      setEditingQuantities({});
      await fetchStorico();
    } catch (error) {
      setEditingPreview(null);
      alert(`Modifica non riuscita: ${error.message}\nRichiedi una nuova anteprima.`);
    } finally {
      setCorrectingId(null);
    }
  };

  // ---------- raggruppo per giorno ----------
  const perGiorno = useMemo(() => {
    const grouped = {};
    const filtered = isConto
      ? movimenti.filter((m) => m.kind === "pagamento" || (m.kind === "consumo" && Number(m.paid || 0) < Number(m.total || 0)))
      : movimenti.filter((m) => m.kind === "consumo");
    for (const m of filtered) {
      const key = m.date || "";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    }
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  }, [movimenti, isConto]);

  // ---------- render ----------
  return (
    <div className="modal d-block" onClick={onClose}>
      <div className="modal-dialog modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
              <h5>{isConto ? "💳 Conto / pagamenti" : "📦 Storico acquisti"}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* pagamento rapido */}
              {isConto && (
            <div className="input-group mb-3">
              <span className="input-group-text">€</span>
              <input
                type="number"
                className="form-control"
                placeholder="Importo pagamento conto"
                value={pagamento}
                onChange={(e) => setPagamento(e.target.value)}
              />

              {usingSeparateDateTime ? (
                <>
                  <input
                    type="date"
                    className="form-control"
                    value={pagamentoData}
                    onChange={(e) => setPagamentoData(e.target.value)}
                  />
                  <input
                    type="time"
                    className="form-control"
                    value={pagamentoOra}
                    onChange={(e) => setPagamentoOra(e.target.value)}
                  />
                </>
              ) : (
                <input
                  type="datetime-local"
                  className="form-control"
                  value={pagamentoData}
                  onChange={(e) => setPagamentoData(e.target.value)}
                />
              )}

              <button className="btn btn-success" onClick={handlePaga} disabled={sending}>
                {sending ? "Invio..." : "Paga"}
              </button>
            </div>
              )}

            {!isConto && editingPurchase && (
              <div className="card border-primary mb-3">
                <div className="card-body">
                  <h6>Modifica acquisto #{editingPurchase.id}</h6>
                  <p className="small text-muted">
                    Modifica quantità e pagamento. I prezzi storici restano invariati.
                  </p>
                  {editingPurchase.products.map(p => (
                    <div key={p.consumptionProductId} className="mb-3">
                      <label className="form-label">
                        {p.name} — quantità attuale: {toMoney(p.qty)}
                      </label>
                      <input
                        className="form-control"
                        type="text"
                        inputMode="decimal"
                        value={editingQuantities[p.consumptionProductId] ?? ""}
                        disabled={correctingId !== null}
                        onChange={e => {
                          setEditingPreview(null);
                          setEditingQuantities(prev => ({
                            ...prev,
                            [p.consumptionProductId]: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  ))}
                  <label className="form-label">Metodo di pagamento</label>
                  <select
                    className="form-select mb-3"
                    value={editingMethod}
                    disabled={correctingId !== null}
                    onChange={e => {
                      setEditingMethod(e.target.value);
                      setEditingPreview(null);
                    }}
                  >
                    <option value="CONTANTI">Contanti</option>
                    <option value="WALLET">Wallet</option>
                  </select>
                  {!editingPurchase.paymentMethod && (
                    <label className="d-flex align-items-start gap-2 mb-3 small">
                      <input
                        type="checkbox"
                        checked={editingLegacyConfirmed}
                        disabled={correctingId !== null}
                        onChange={e => {
                          setEditingLegacyConfirmed(e.target.checked);
                          setEditingPreview(null);
                        }}
                      />
                      Ho verificato che questo acquisto storico era stato pagato
                      in contanti e non tramite Wallet.
                    </label>
                  )}
                  <label className="form-label">Motivazione obbligatoria</label>
                  <textarea
                    className="form-control mb-3"
                    rows="2"
                    maxLength={1000}
                    value={editingReason}
                    disabled={correctingId !== null}
                    onChange={e => {
                      setEditingReason(e.target.value);
                      setEditingPreview(null);
                    }}
                  />
                  {editingPreview && (
                    <div className="border rounded p-3 mb-3">
                      <strong>Anteprima contabile</strong>
                      <div>Totale attuale: €{toMoney(editingPreview.preview.purchase?.current_total)}</div>
                      <div>Nuovo totale: €{toMoney(editingPreview.preview.purchase?.corrected_total)}</div>
                      <div>Variazione punti: {editingPreview.preview.accounting?.points_delta ?? "0"}</div>
                      <div>Wallet PROMO: €{toMoney(editingPreview.preview.accounting?.wallet?.promo_delta)}</div>
                      <div>Wallet NORMAL: €{toMoney(editingPreview.preview.accounting?.wallet?.normal_delta)}</div>
                      <div className="fw-bold mt-2">
                        {Number(editingPreview.preview.accounting?.cash_delta ?? 0) > 0
                          ? `Contanti da incassare: €${toMoney(editingPreview.preview.accounting.cash_delta)}`
                          : Number(editingPreview.preview.accounting?.cash_delta ?? 0) < 0
                            ? `Contanti da rimborsare: €${toMoney(-Number(editingPreview.preview.accounting.cash_delta))}`
                            : "Nessuna differenza in contanti"}
                      </div>
                    </div>
                  )}
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={correctingId !== null}
                      onClick={anteprimaModificaAcquisto}
                    >
                      Calcola anteprima
                    </button>
                    {editingPreview && (
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        disabled={correctingId !== null}
                        onClick={salvaModificaAcquisto}
                      >
                        Conferma e salva
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={correctingId !== null}
                      onClick={() => {
                        setEditingPurchase(null);
                        setEditingPreview(null);
                      }}
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <p>Caricamento…</p>
            ) : perGiorno.length === 0 ? (
                <p>{isConto ? "Nessun movimento conto registrato." : "Nessun acquisto registrato."}</p>
            ) : (
              perGiorno.map(([giorno, righe], idx) => {
                const consumi = righe.filter(r => r.kind === "consumo");
                const pagamenti = righe
                  .filter(r => r.kind === "pagamento")
                  .sort((a, b) => ((a.time || "").localeCompare(b.time || "")));

                const totaleConsumi = consumi.reduce((s, r) => s + (r.total || 0), 0);
                const totalePagamenti = pagamenti.reduce((s, r) => s + (r.amount || 0), 0);

                return (
                  <div key={giorno} className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">📅 {formatDate(giorno)}</h6>                      </div>

                      {consumi.length > 0 && (
                        <div className="mb-3">
                          <div className="fw-bold mb-2">🛒 Acquisti</div>
                          {consumi.map((r, i) => (
                            <div key={`c-${i}`} className="border rounded p-2 mb-2 bg-light">
                              <div className="text-muted small mb-1">🕒 {formatTime(r.time) || "Ora non indicata"}</div>
                              {r.products?.length > 0 ? (
                                <ul className="mb-2 ps-3">
                                  {r.products.map((p, j) => (
                                    <li key={j}>
                                      {toMoney(p.qty)}× {p.name} @ €{toMoney(p.unit)} = €{toMoney(p.subtotal)}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-muted small">Nessun dettaglio prodotto.</div>
                              )}
                              <div><strong>Totale acquisto:</strong> €{toMoney(r.total)}</div>
                              <div className="small text-muted">
                                Metodo: {r.paymentMethod || "Non registrato (storico)"}
                                {r.corrected ? " — Rettificato" : ""}
                              </div>
                              {!isConto &&
                                isSede &&
                                r.id &&
                                ["CONTANTI", "WALLET", ""].includes(r.paymentMethod) &&
                                Number(r.paid) === Number(r.total) &&
                                r.products?.length > 0 &&
                                r.products.every(p => p.consumptionProductId) && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm mt-2"
                                    disabled={correctingId !== null}
                                    onClick={() => apriModificaAcquisto(r)}
                                  >
                                    Modifica acquisto
                                  </button>
                                )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="border-top pt-2 mt-2">
                        <div className="fw-bold mb-2">📊 Riepilogo giornata</div>
                        <div className="d-flex justify-content-between">
                          <span>Totale consumi</span>
                          <strong>€{toMoney(totaleConsumi)}</strong>
                        </div>
                      </div>
                    </div>

                    {idx < perGiorno.length - 1 && <div className="border-bottom" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoricoConsumiModal;
